import { initMetrics, sendEventMetric } from "@expert/cloudwatch-metrics";
import { getLogger, initLoggingTransmission } from "@expert/logging";
import { env } from "@/config";
import { findPilotGroups } from "../../pilot/pilot";

const logger = getLogger({ module: "UserCache" });

/**
 * Interface representing authenticated user data from SSO.
 * Contains the raw authentication token and user profile information.
 */
export interface CachedUser {
    /** Authentication token */
    access_token: string;
    /** User profile information from authentication provider */
    profile: {
        sub: string;
        employeeID: string | null;
        given_name: string | null;
        family_name: string | null;
        userPrincipalName: string | null;
        email: string | null;
        /** User's Active Directory Groups */
        groups: string | string[];
        partner: string | undefined;
        org?: string;
        salesforceId?: string;
    };
}

/**
 * Service class for managing authenticated user data.
 * Provides safe access to user properties and initializes services that depend on user authentication.
 */
export class UserCache {
    private _cachedUser?: CachedUser;

    public setUser(user: CachedUser, userFriendlyName?: string) {
        this._cachedUser = user;
        // TODO: Make it's more elegant. For example by emitting an event
        initLoggingTransmission({ accessToken: user.access_token, collectorUrl: env.logCollectorUrl });
        initMetrics({ accessToken: user.access_token, collectorUrl: env.metricsCollectorUrl });
        void sendEventMetric("sso.authenticated");

        if (!this._cachedUser.profile.employeeID && userFriendlyName) {
            this._cachedUser.profile.employeeID = userFriendlyName;
        }
    }

    public get ssoAccessToken() {
        return this._cachedUser?.access_token;
    }

    public get identity() {
        return this._cachedUser?.profile.sub;
    }

    public get employeeId() {
        return this._cachedUser?.profile.employeeID ?? null;
    }

    public get salesforceId() {
        return this._cachedUser?.profile.salesforceId ?? null;
    }

    public get userPrincipalName() {
        return this._cachedUser?.profile.userPrincipalName ?? null;
    }

    public get userEmail() {
        return this._cachedUser?.profile.email ?? null;
    }

    public get givenName() {
        return this._cachedUser?.profile.given_name ?? null;
    }

    public get familyName() {
        return this._cachedUser?.profile.family_name ?? null;
    }

    /**
     * Processes an array of group values, ensuring all elements are valid strings
     * and filtering out blank strings or invalid values
     * @private
     */
    private processGroupValues(groups: unknown): string[] {
        if (!groups) return [];

        // Handle case where groups is a non-blank string
        if (typeof groups === "string") {
            return groups.trim() ? [groups] : [];
        }

        if (Array.isArray(groups)) {
            // Filter out non-strings and all-whitespace strings (preserve original whitespace)
            const validGroups = groups.filter((item): item is string => typeof item === "string" && item.trim() !== "");

            if (validGroups.length !== groups.length) {
                logger.warn(
                    {
                        groups,
                        validGroups: validGroups.length,
                        identity: this.identity,
                    },
                    "Some group values were invalid or empty and were filtered out",
                );
            }

            return validGroups;
        }

        logger.error(
            { groups, groupsType: typeof groups, identity: this.identity },
            "Unexpected groups type received from SSO. This could cause issues with permission checks",
        );
        return [];
    }

    /** The user's Active Directory groups *( Not to be confused with Orbit groups )* */
    public get groups(): string[] {
        return this.processGroupValues(this._cachedUser?.profile.groups);
    }

    public get partner() {
        return this._cachedUser?.profile.partner;
    }

    public containsGroup(group: string): boolean {
        return this.groups.includes(group);
    }

    public get pilots() {
        return findPilotGroups(this.groups);
    }

    public get org() {
        return this._cachedUser?.profile.org;
    }
}

/**
 * Singleton instance of the UserCache service.
 * Use this to access authenticated user information throughout the application.
 */
export const userCache = new UserCache();
